import React, { useContext, useEffect, useRef, useState } from 'react';
import {
  Box,
  Chip,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  CameraAlt as CameraAltIcon,
  Cameraswitch as CameraswitchIcon,
  Replay as ReplayIcon,
  Add as AddIcon,
} from '@mui/icons-material';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';

import { AppContext } from '../../../context/Context';
import { SocketContext } from '../../../context/SocketContext';
import {
  verticallyCenteredLabelSx,
  fieldHelpSx,
} from '../../../theme/fieldTokens';
import { headerCellSx } from '../../../theme/tableTokens';
import { statusTone } from '../../../utils/errorTicketStatus';

// Free text typed at a till: 2000 characters, possibly without a single space
const WRAP = { overflowWrap: 'anywhere', wordBreak: 'break-word' };

// Same step Spillplan takes for its modal table: the screen tokens are too small here
const modalHeaderCellSx = {
  ...headerCellSx,
  fontSize: '17px',
  padding: '18px 12px',
};

const PANEL_SX = {
  px: 3,
  py: 2,
  borderRadius: '14px',
  backgroundColor: '#151d3b',
  border: '1px solid #2a3a6e',
};

// Matches the ENUM in migrations/2026-08-20-error-ticket-category.sql. Only a
// technical fault carries a terminal number
const CATEGORIES = ['economy', 'technical', 'other'];
const CATEGORY_TECHNICAL = 'technical';

function ModalErrorTicket() {
  const { t } = useTranslation();
  const {
    hall,
    operator,
    session,
    updateShowModalErrorTicket,
    updateFocusOnMainScreen,
  } = useContext(AppContext);
  const { socket } = useContext(SocketContext);

  // 'list' is what opens: most of the time this is checked, not filed
  const [view, setView] = useState('list');
  const [tickets, setTickets] = useState([]);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [signedByID, setSignedByID] = useState('');
  const [signedBy, setSignedBy] = useState('');
  const [operators, setOperators] = useState([]);
  // What kind of fault, and — only for a technical one — which machine
  const [category, setCategory] = useState('');
  const [terminalNumber, setTerminalNumber] = useState('');
  const [photoUrl, setPhotoUrl] = useState(null);
  const [captured, setCaptured] = useState(null);
  const [busy, setBusy] = useState(false);
  // A fault without a picture is allowed, but it is asked about before it is filed
  const [confirmNoPhoto, setConfirmNoPhoto] = useState(false);

  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const [devices, setDevices] = useState([]);
  const [cameraIndex, setCameraIndex] = useState(0);

  useEffect(() => {
    if (!socket) return;

    const handleList = (data) =>
      setTickets(Array.isArray(data?.tickets) ? data.tickets : []);

    const handleOperators = (data) =>
      setOperators(Array.isArray(data?.operators) ? data.operators : []);

    const handleCreated = (data) => {
      setBusy(false);
      if (!data?.ok) {
        toast.error(t('error.error'));
        return;
      }
      toast.success(t('errorTicket.filed'));
      setTitle('');
      setDescription('');
      setCategory('');
      setTerminalNumber('');
      setPhotoUrl(null);
      setCaptured(null);
      setView('list');
    };

    const handleUpload = (data) => {
      setBusy(false);
      if (!data?.url) {
        toast.error(t('errorTicket.photoFailed'));
        return;
      }
      setPhotoUrl(data.url);
      toast.success(t('errorTicket.photoAttached'));
    };

    socket.on('listErrorTickets', handleList);
    socket.on('listOperators', handleOperators);
    socket.on('newErrorTicket', handleCreated);
    socket.on('uploadImageSignature', handleUpload);

    socket.emit('listErrorTickets', { bingoName: hall });
    socket.emit('listOperators', {});

    return () => {
      socket.off('listErrorTickets', handleList);
      socket.off('listOperators', handleOperators);
      socket.off('newErrorTicket', handleCreated);
      socket.off('uploadImageSignature', handleUpload);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [socket, hall]);

  // MenuButtons re-asks the list on any change and this table gets that answer too

  // HeaderSearcher takes the focus back after any click outside it; stand it down
  useEffect(() => {
    updateFocusOnMainScreen(false);
    return () => updateFocusOnMainScreen(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const stopCamera = () => {
    streamRef.current?.getTracks().forEach((track) => track.stop());
    streamRef.current = null;
  };

  // A live preview, same as ModalFullUserRegistration — never a file picker
  useEffect(() => {
    if (view !== 'new' || captured) {
      stopCamera();
      return;
    }

    let cancelled = false;

    (async () => {
      try {
        const first = await navigator.mediaDevices.getUserMedia({
          video: true,
        });
        // Labels only arrive once permission is granted, so enumerate after asking
        const all = await navigator.mediaDevices.enumerateDevices();
        const videoDevices = all.filter((d) => d.kind === 'videoinput');

        if (cancelled) {
          first.getTracks().forEach((track) => track.stop());
          return;
        }
        setDevices(videoDevices);

        // The first stream is whatever the browser picked; swap if another was chosen
        let live = first;
        const wanted = videoDevices[cameraIndex]?.deviceId;
        if (wanted && cameraIndex > 0) {
          first.getTracks().forEach((track) => track.stop());
          live = await navigator.mediaDevices.getUserMedia({
            video: { deviceId: { exact: wanted } },
          });
        }

        streamRef.current = live;
        if (videoRef.current) {
          videoRef.current.srcObject = live;
          videoRef.current.play();
        }
      } catch (err) {
        console.error('Error accessing the camera:', err);
        toast.error(t('errorTicket.cameraFailed'));
      }
    })();

    return () => {
      cancelled = true;
      stopCamera();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view, captured, cameraIndex]);

  // A modal closed mid-shot must not leave the camera light on
  useEffect(() => stopCamera, []);

  const handleCapture = () => {
    const video = videoRef.current;
    if (!video || !video.videoWidth) return;

    const canvas = document.createElement('canvas');
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    canvas.getContext('2d').drawImage(video, 0, 0, canvas.width, canvas.height);
    setCaptured(canvas.toDataURL('image/png'));
  };

  // Same channel the signature photos use, so pictures live in one place
  const handleUsePhoto = () => {
    if (!captured) return;

    const binary = atob(captured.split(',')[1]);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);

    setBusy(true);
    socket.emit('uploadImageSignature', {
      fileBuffer: bytes,
      fileName: `ticket_${hall}_${Date.now()}.png`,
      bingoName: hall,
      operator,
      session,
    });
  };

  const handleSubmit = () => {
    if (!title.trim() || !description.trim()) {
      toast.error(t('errorTicket.titleAndDescriptionRequired'));
      return;
    }
    const signValid = operators.length ? signedByID !== '' : signedBy.trim();
    if (!signValid) {
      toast.error(t('errorTicket.signRequired'));
      return;
    }

    if (!category) {
      toast.error(t('errorTicket.categoryRequired'));
      return;
    }
    // Which machine is the whole point of a technical report
    if (category === CATEGORY_TECHNICAL && !terminalNumber.trim()) {
      toast.error(t('errorTicket.terminalRequired'));
      return;
    }

    // A picture is what makes a fault report useful, and one that was taken but
    // never attached is the easiest thing to lose here
    if (!photoUrl) {
      setConfirmNoPhoto(true);
      return;
    }

    fileTicket();
  };

  const fileTicket = () => {
    setConfirmNoPhoto(false);
    setBusy(true);
    socket.emit('newErrorTicket', {
      bingoName: hall,
      operator,
      sessionID: session,
      title: title.trim(),
      description: description.trim(),
      category,
      terminalNumber:
        category === CATEGORY_TECHNICAL ? terminalNumber.trim() : null,
      signedBy: signedBy.trim() || null,
      signedByID: signedByID === '' ? null : Number(signedByID),
      photoUrl,
    });
  };

  // The same set the header counts on its button, so both say one number
  const openCount = tickets.filter(
    (x) => x.status === 'open' || x.status === 'in_progress'
  ).length;

  const renderList = () => (
    <>
      <p>{t('errorTicket.openCount', { count: openCount })}</p>

      {tickets.length === 0 ? (
        <Typography sx={{ ...fieldHelpSx, mt: 3, textAlign: 'left' }}>
          {t('errorTicket.none')}
        </Typography>
      ) : (
        <Box
          sx={{
            mt: 2,
            textAlign: 'left',
            maxHeight: '56vh',
            overflow: 'auto',
            borderRadius: '10px',
            border: '1px solid #2a3a6e',
            // muted scrollbar; sp-scroll's #365ab8 thumb reads as a blue rule here
            '&::-webkit-scrollbar': { width: '8px' },
            '&::-webkit-scrollbar-track': { background: 'transparent' },
            '&::-webkit-scrollbar-thumb': {
              background: '#2a3a6e',
              borderRadius: '6px',
            },
          }}
        >
          <Table
            stickyHeader
            sx={{
              '& tbody tr:nth-of-type(odd)': { backgroundColor: '#232a45' },
              '& tbody tr:nth-of-type(even)': { backgroundColor: '#1a2036' },
              '& tbody td': {
                color: '#e6eaf2',
                fontSize: '19px',
                padding: '14px 12px',
                borderBottom: '1px solid #2a3a6e',
                verticalAlign: 'top',
              },
            }}
          >
            <TableHead>
              <TableRow>
                <TableCell sx={{ ...modalHeaderCellSx, width: '70px' }}>
                  #
                </TableCell>
                <TableCell sx={{ ...modalHeaderCellSx, width: '190px' }}>
                  {t('errorTicket.colDate')}
                </TableCell>
                <TableCell sx={modalHeaderCellSx}>
                  {t('errorTicket.fieldTitle')}
                </TableCell>
                <TableCell sx={modalHeaderCellSx}>
                  {t('errorTicket.colDescription')}
                </TableCell>
                <TableCell sx={{ ...modalHeaderCellSx, width: '180px' }}>
                  {t('errorTicket.signedBy')}
                </TableCell>
                <TableCell sx={{ ...modalHeaderCellSx, width: '210px' }}>
                  {t('errorTicket.colStatus')}
                </TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {tickets.map((ticket) => {
                const tone = statusTone(ticket.status);
                return (
                  <TableRow key={ticket.id}>
                    <TableCell sx={{ fontWeight: 700 }}>{ticket.id}</TableCell>
                    <TableCell sx={{ color: '#8fa3d4', whiteSpace: 'nowrap' }}>
                      {ticket.createdAt}
                    </TableCell>
                    <TableCell sx={{ maxWidth: '260px', ...WRAP }}>
                      {ticket.title}
                    </TableCell>
                    <TableCell sx={{ maxWidth: '320px' }}>
                      {/* Two lines is enough to recognise a fault already known */}
                      <Box
                        sx={{
                          ...WRAP,
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden',
                        }}
                      >
                        {ticket.description}
                      </Box>
                    </TableCell>
                    <TableCell>{ticket.signedBy || '—'}</TableCell>
                    <TableCell>
                      <Chip
                        label={t(tone.labelKey)}
                        sx={{
                          color: tone.color,
                          border: `1px solid ${tone.color}`,
                          backgroundColor: 'transparent',
                          fontSize: '16px',
                        }}
                      />
                      {/* The note the office sent back travels with the status */}
                      {ticket.statusNote && (
                        <Typography sx={{ ...fieldHelpSx, mt: 0.5 }}>
                          {ticket.statusNote}
                        </Typography>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Box>
      )}

      <div className="react-confirm-alert-button-group">
        <button className="btn-custom-yes" onClick={() => setView('new')}>
          <AddIcon style={{ fontSize: '1.2em' }} />
          {t('errorTicket.new')}
        </button>
        <button
          className="btn-custom-no"
          onClick={() => updateShowModalErrorTicket(false)}
        >
          <CancelIcon style={{ fontSize: '1.2em' }} />
          {t('sales.cancel')}
        </button>
      </div>
    </>
  );

  const renderForm = () => (
    <>
      {/* Left what happened, right the picture of it: a fault is described and
          shown at the same time, and flipping between the two loses the thread */}
      <Box sx={{ display: 'flex', gap: 3, mt: 2, textAlign: 'left' }}>
        <Box
          sx={{
            ...PANEL_SX,
            width: '560px',
            flexShrink: 0,
            display: 'flex',
            flexDirection: 'column',
            gap: 3,
            py: 3,
          }}
        >
          <TextField
            autoComplete="off"
            label={t('errorTicket.fieldTitle') + '*'}
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            inputProps={{ maxLength: 200 }}
            InputLabelProps={{ sx: verticallyCenteredLabelSx }}
            className="modal-textfield"
            style={{ width: '100%' }}
            autoFocus
          />
          {/* Chunky buttons, not a select: this is a touch screen, and a
              <TextField select> with no items opens an invisible modal that
              swallows the keyboard — see the ansatte notes */}
          <Box>
            <Typography sx={{ ...fieldHelpSx, textAlign: 'left', mb: 1 }}>
              {t('errorTicket.categoryLabel')}
            </Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              {CATEGORIES.map((key) => (
                <button
                  key={key}
                  type="button"
                  className={`btn-pick${category === key ? ' is-on' : ''}`}
                  onClick={() => setCategory(key)}
                >
                  {t(`errorTicket.category_${key}`)}
                </button>
              ))}
            </Box>
          </Box>

          {/* Only where it means something; an economy report has no machine */}
          {category === CATEGORY_TECHNICAL && (
            <TextField
              autoComplete="off"
              label={t('errorTicket.terminalNumber') + '*'}
              value={terminalNumber}
              onChange={(e) => setTerminalNumber(e.target.value)}
              inputProps={{ maxLength: 50 }}
              InputLabelProps={{ sx: verticallyCenteredLabelSx }}
              className="modal-textfield"
              style={{ width: '100%' }}
            />
          )}

          <TextField
            autoComplete="off"
            label={t('errorTicket.fieldDescription') + '*'}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            multiline
            minRows={10}
            sx={{
              flex: 1,
              // The wrapper grows with the panel, but the textarea keeps the height
              // its row count gave it — that is the gap underneath
              '& .MuiInputBase-root': {
                height: '100%',
                alignItems: 'flex-start',
              },
              '& .MuiInputBase-inputMultiline': {
                height: '100% !important',
                overflowY: 'auto',
              },
            }}
            inputProps={{ maxLength: 2000 }}
            className="modal-textfield"
            style={{ width: '100%' }}
          />
          {operators.length === 0 ? (
            <TextField
              autoComplete="off"
              label={t('errorTicket.signedBy') + '*'}
              value={signedBy}
              onChange={(e) => setSignedBy(e.target.value)}
              inputProps={{ maxLength: 100 }}
              InputLabelProps={{ sx: verticallyCenteredLabelSx }}
              className="modal-textfield"
              style={{ width: '100%' }}
            />
          ) : (
            <TextField
              select
              label={t('errorTicket.signedBy') + '*'}
              value={signedByID}
              onChange={(e) => {
                setSignedByID(e.target.value);
                const picked = operators.find(
                  (o) => o.id === Number(e.target.value)
                );
                setSignedBy(picked ? picked.name : '');
              }}
              InputLabelProps={{ sx: verticallyCenteredLabelSx }}
              className="modal-textfield"
              style={{ width: '100%' }}
              SelectProps={{
                MenuProps: {
                  // The menu portals out of the modal, which sits at z-index 999999
                  sx: { zIndex: 1000000 },
                  PaperProps: {
                    sx: {
                      backgroundColor: '#151d3b',
                      color: '#c9d6f2',
                      maxHeight: 360,
                      '& .MuiMenuItem-root': {
                        fontSize: '20px',
                        minHeight: 56,
                      },
                    },
                  },
                },
              }}
            >
              {operators.map((o) => (
                <MenuItem key={o.id} value={o.id}>
                  {o.name}
                </MenuItem>
              ))}
            </TextField>
          )}
        </Box>

        <Box sx={{ ...PANEL_SX, flex: 1, minWidth: 0 }}>
          <Typography sx={{ ...fieldHelpSx, mb: 1 }}>
            {t('errorTicket.photoHelp')}
          </Typography>

          <Box
            sx={{
              borderRadius: '10px',
              overflow: 'hidden',
              backgroundColor: '#0b1229',
              height: '420px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            {captured ? (
              <img
                src={captured}
                alt=""
                style={{
                  maxWidth: '100%',
                  maxHeight: '100%',
                  objectFit: 'contain',
                }}
              />
            ) : (
              <video
                ref={videoRef}
                muted
                playsInline
                style={{
                  maxWidth: '100%',
                  maxHeight: '100%',
                  objectFit: 'contain',
                }}
              />
            )}
          </Box>

          <Box
            sx={{ display: 'flex', gap: 1, mt: 2, justifyContent: 'center' }}
          >
            {captured ? (
              <>
                <button
                  className="btn-custom-camara"
                  onClick={handleUsePhoto}
                  disabled={busy || Boolean(photoUrl)}
                >
                  <CheckCircleIcon style={{ fontSize: '1.2em' }} />
                  {photoUrl
                    ? t('errorTicket.photoAttached')
                    : t('errorTicket.photoUse')}
                </button>
                <button
                  className="btn-custom-camara-back"
                  onClick={() => {
                    setCaptured(null);
                    setPhotoUrl(null);
                  }}
                >
                  <ReplayIcon style={{ fontSize: '1.2em' }} />
                  {t('errorTicket.photoRetake')}
                </button>
              </>
            ) : (
              <>
                <button className="btn-custom-camara" onClick={handleCapture}>
                  <CameraAltIcon style={{ fontSize: '1.2em' }} />
                  {t('errorTicket.photoTake')}
                </button>
                {devices.length > 1 && (
                  <button
                    className="btn-custom-camara-cancel"
                    onClick={() =>
                      setCameraIndex((i) => (i + 1) % devices.length)
                    }
                  >
                    <CameraswitchIcon style={{ fontSize: '1.2em' }} />
                    {t('errorTicket.photoSwitch')}
                  </button>
                )}
              </>
            )}
          </Box>
        </Box>
      </Box>

      <div className="react-confirm-alert-button-group">
        <button
          className="btn-custom-yes"
          onClick={handleSubmit}
          disabled={busy}
        >
          <CheckCircleIcon style={{ fontSize: '1.2em' }} />
          {t('sales.confirm')}
        </button>
        <button className="btn-custom-no" onClick={() => setView('list')}>
          <CancelIcon style={{ fontSize: '1.2em' }} />
          {t('endShift.back')}
        </button>
      </div>
    </>
  );

  // Sits outside the modal body: an overlay inside it would be cut by its own scroll
  const renderNoPhotoConfirm = () => (
    <div className="react-confirm-alert-overlay" style={{ zIndex: 1000001 }}>
      <div className="react-confirm-alert">
        <div
          className="react-confirm-alert-body modal-status"
          style={{ boxSizing: 'border-box', width: '700px', maxWidth: '92vw' }}
        >
          <h1>{t('errorTicket.noPhotoTitle')}</h1>
          <p>
            {captured
              ? t('errorTicket.noPhotoUnused')
              : t('errorTicket.noPhotoText')}
          </p>

          <div className="react-confirm-alert-button-group">
            <button
              className="btn-custom-yes"
              onClick={fileTicket}
              disabled={busy}
            >
              <CheckCircleIcon style={{ fontSize: '1.2em' }} />
              {t('errorTicket.noPhotoSend')}
            </button>
            <button
              className="btn-custom-no"
              onClick={() => setConfirmNoPhoto(false)}
            >
              <CancelIcon style={{ fontSize: '1.2em' }} />
              {t('sales.cancel')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <>
      <div id="react-confirm-alert">
        <div className="react-confirm-alert-overlay">
          <div className="react-confirm-alert">
            <div
              className="react-confirm-alert-body modal-status"
              style={{
                boxSizing: 'border-box',
                width: view === 'new' ? '1320px' : '1240px',
                maxWidth: '96vw',
                maxHeight: '88vh',
                overflowY: 'auto',
              }}
            >
              <h1>{t('errorTicket.title')}</h1>
              {view === 'list' ? renderList() : renderForm()}
            </div>
          </div>
        </div>
      </div>

      {confirmNoPhoto && renderNoPhotoConfirm()}
    </>
  );
}

export default ModalErrorTicket;
